import React, { useState, useEffect, useRef } from 'react';
import { Link , useNavigate } from 'react-router-dom';
import { X, ChevronDown } from 'lucide-react';

const MegaMenu = ({ products }) => {
  // const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const dropdownRef = useRef(null);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);

  // Group products by category
  const groupedProducts = products?.products?.reduce((acc, product) => {
    if (product?.category === "Industrial Ultrasonic Cleaning Machine") {
      if (!acc.industrial) acc.industrial = [];
      acc.industrial.push(product);
    } else {
      if (!acc.other) acc.other = [];
      acc.other.push(product);
    }
    return acc;
  }, {}) || { industrial: [], descaling: [], other: [] };

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProductDropdownOpen(false);
        setExpandedCategory(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLinkClick = (path) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  const Logo = () => (
    // <span className="bg-gradient-to-r from-green-300 via-blue-500 to-purple-600 bg-clip-text text-3xl font-extrabold text-transparent sm:text-5xl">
    //   ULTRONICS
    // </span>
    <img className="h-16 md:h-full py-1 px-1"src="../ultronicsLogo.png" alt="Ultronics Logo"/>
  );

  return (
    <header className="bg-white w-full top-0 z-50 shadow-sm sticky">
      <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-1">
        <div className="flex h-16 items-center justify-between">
          <div className="flex-1 md:flex md:items-center md:gap-12 h-full">
            <Link className="block h-full" to="/">
              <span className="sr-only">Home</span>
              <Logo />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav aria-label="Global" className="hidden md:block">
            <ul className="flex items-center gap-6 text-sm font-medium">
              <li>
                <Link className="text-gray-500 transition hover:text-gray-700" to="/">Home</Link>
              </li>
              <li>
                <Link className="text-gray-500 transition hover:text-gray-700" to="/about">About</Link>
              </li>
              <li className="relative" ref={dropdownRef}>
                <div className="relative">
                  <button 
                    onClick={()=> isProductDropdownOpen?setIsProductDropdownOpen(false):setIsProductDropdownOpen(true)} 
                    className="flex items-center text-gray-500 transition hover:text-gray-700 py-4"
                  >
                    <Link to="/products"><span>Products</span></Link>
                    <ChevronDown 
                      className={`ml-1 h-4 w-4 transition-transform duration-200 ${isProductDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  {/* Products Dropdown - Desktop */}
                  {isProductDropdownOpen && (
                    <div className="absolute left-0 mt-0 w-64 bg-white border rounded-lg shadow-lg max-h-[calc(100vh-120px)] overflow-y-auto no-scrollbar">
                      {/* Industrial Ultrasonic Category */}
                      <div 
                        className="relative"
                        onClick={() => setExpandedCategory((currState)=> currState === "industrial"?null :"industrial")}
                      >
                        <div className="px-4 py-3 hover:bg-gray-50 cursor-pointer">
                          <span className="font-medium text-gray-800">Industrial Ultrasonic Cleaning Machine</span>
                          <ChevronDown 
                            className={`ml-1 h-4 w-4 inline-block transition-transform duration-200 ${expandedCategory === 'industrial' ? 'rotate-180' : ''}`} 
                          />
                        </div>
                        
                        {expandedCategory === 'industrial' && (
                          <div className="bg-gray-50 py-2">
                            {groupedProducts.industrial?.map((product) => (
                              <Link
                                key={product.id}
                                to={`/products/${product.id}`}
                                onClick={()=>{setIsProductDropdownOpen(false);setExpandedCategory(null)}}
                                className="block px-8 py-2 text-sm text-gray-600 hover:bg-gray-100 transition-colors"
                              >
                                {product.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Descaling Machines Category */}
        
                      {/* Other Products */}
                      {groupedProducts.other?.map((product) => (
                        <Link
                          key={product.id}
                          to={`/products/${product.id}`}
                          className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                          {product.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </li>
              <li>
                <Link className="text-gray-500 transition hover:text-gray-700" to="/e-catalogue">E-catalogue</Link>
              </li>
              <li>
                <Link className="text-gray-500 transition hover:text-gray-700" to="/contact">Contact Us</Link>
              </li>
            </ul>
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button 
              onClick={toggleMenu}
              className="text-gray-600 hover:text-gray-700 focus:outline-none"
              aria-label="menu button mobile"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden fixed inset-0 bg-white z-50 overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-4 py-4 flex items-center justify-between">
              <Link className="block" to="/" onClick={() => setIsMenuOpen(false)}>
                <Logo />
              </Link>
              <button 
                onClick={toggleMenu}
                className="text-gray-600 hover:text-gray-700 focus:outline-none"
                aria-label="menu button mobile"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 px-4 py-6">
              <ul className="space-y-4">
                <li>
                  <button onClick={() => handleLinkClick('/')} className="block text-lg font-medium text-gray-700 hover:text-gray-900 w-full text-left">
                    Home
                  </button>
                </li>
                <li>
                  <button onClick={() => handleLinkClick('/about')} className="block text-lg font-medium text-gray-700 hover:text-gray-900 w-full text-left">
                    About
                  </button>
                </li>
                <li className="space-y-2">
                  <button 
                    onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
                    className="flex items-center justify-between w-full text-lg font-medium text-gray-700 hover:text-gray-900"
                  >
                    <span onTouchEnd={()=> handleLinkClick(`/products/`)}>Products</span>
                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isMobileProductsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isMobileProductsOpen && (
                    <div className="pl-4 space-y-4 mt-2">
                      <div className="space-y-2">
                        <button 
                          onClick={() => setExpandedCategory(expandedCategory === 'industrial' ? null : 'industrial')}
                          className="flex items-center justify-between w-full text-base font-medium text-gray-700 hover:text-gray-900"
                        >
                          <span>Industrial Ultrasonic Cleaning Machine</span>
                          <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${expandedCategory === 'industrial' ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {expandedCategory === 'industrial' && (
                          <div className="pl-4 space-y-2 bg-gray-50 rounded-lg p-4">
                            {groupedProducts.industrial?.map((product) => (
                              <button
                                key={product.id}
                                onTouchEnd={() => handleLinkClick(`/products/${product.id}`)}
                                className="block py-2 text-gray-600 hover:text-gray-900 w-full text-left text-sm"
                              >
                                {product.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Descaling Machines Mobile Dropdown */}


                      {groupedProducts.other?.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => handleLinkClick(`/products/${product.id}`)}
                          className="block py-2 text-gray-600 hover:text-gray-900 w-full text-left text-sm"
                        >
                          {product.name}
                        </button>
                      ))}
                    </div>
                  )}
                </li>
                <li>
                  <button onClick={() => handleLinkClick('/e-catalogue')} className="block text-lg font-medium text-gray-700 hover:text-gray-900 w-full text-left">
                    E-catalogue
                  </button>
                </li>
                <li>
                  <button onClick={() => handleLinkClick('/contact')} className="block text-lg font-medium text-gray-700 hover:text-gray-900 w-full text-left">
                    Contact Us
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default MegaMenu;